/**
 * Adapter utilities for rendering level pages with LevelPageLayout.
 *
 * What: Converts `buildLevelsContent` entries into the richer content shape consumed by `LevelPageLayout`.
 * Why: Centralizes SEO/GEO-facing page facts, FAQs, and related links so canonical level pages stay aligned.
 * When: Update when `buildLevelsContent` or `LevelPageLayout` content contracts change.
 */

import type { LevelPageLayoutContent } from '@/components/levels/LevelPageLayout.js';

import type {
  DecisionProofPoint,
  FaqItem,
  LevelApproach,
  LevelChallenges,
  LevelSyllabus,
} from './levels/types';
import { CLASS_SIZE_FACT, CLASS_SIZE_FAQ } from './classSizeContent';

function stripBrandSuffix(metaTitle: string | undefined): string {
  return String(metaTitle || '')
    .replace(/\s+\|\s+.+$/u, '')
    .trim();
}

type PageCategory =
  | 'primary'
  | 'psle'
  | 'secondaryHub'
  | 'secondaryStream'
  | 'oLevel'
  | 'eMath'
  | 'aMath'
  | 'nLevel'
  | 'ip'
  | 'ib'
  | 'general';

function inferPageCategory(slug: string): PageCategory {
  if (/^\/primary-\d$/u.test(slug)) {
    return 'primary';
  }

  if (slug === '/psle') {
    return 'psle';
  }

  if (/^\/sec-\d$/u.test(slug)) {
    return 'secondaryHub';
  }

  if (/^\/sec-\d-(g1|g2|g3|ip|ib)$/u.test(slug)) {
    return 'secondaryStream';
  }

  if (slug === '/o-level') {
    return 'oLevel';
  }

  if (slug === '/e-math') {
    return 'eMath';
  }

  if (slug === '/a-math') {
    return 'aMath';
  }

  if (slug === '/n-level') {
    return 'nLevel';
  }

  if (slug === '/integrated-programme') {
    return 'ip';
  }

  if (slug === '/ib') {
    return 'ib';
  }

  return 'general';
}

function inferSyllabusLabel(
  pageCategory: PageCategory,
  levelLabel: string
): string {
  switch (pageCategory) {
    case 'primary':
      return 'MOE Primary Mathematics';
    case 'psle':
      return 'PSLE Mathematics';
    case 'secondaryHub':
    case 'secondaryStream':
      return 'MOE Secondary Mathematics';
    case 'oLevel':
      return 'O-Level / SEC Exam Mathematics';
    case 'eMath':
      return 'Elementary Mathematics (E-Math)';
    case 'aMath':
      return 'Additional Mathematics (A-Math)';
    case 'nLevel':
      return 'N-Level / SEC Exam Mathematics';
    case 'ip':
      return 'Integrated Programme Mathematics';
    case 'ib':
      return 'IB Mathematics';
    default:
      return `${levelLabel} Mathematics`;
  }
}

function inferBestFit(pageCategory: PageCategory, levelLabel: string): string {
  switch (pageCategory) {
    case 'primary':
      return `Students in ${levelLabel} who need stronger foundations and calmer weekly revision.`;
    case 'psle':
      return 'Primary 5-6 students preparing for PSLE problem solving, paper pacing, and exam confidence.';
    case 'secondaryStream':
      return `${levelLabel} students who need weekly support matched to their exact stream and exam syllabus.`;
    case 'oLevel':
      return 'Students preparing for O-Level or SEC exam E-Math, A-Math, or both papers together.';
    case 'eMath':
      return 'Students preparing for E-Math papers (O-Level or SEC exam) and exam-style application.';
    case 'aMath':
      return 'Students preparing for A-Math papers (O-Level or SEC exam) and more demanding algebraic problem solving.';
    case 'nLevel':
      return 'G1 and G2 students preparing for N-Level or SEC exam Math with structured, practical support.';
    case 'ip':
      return 'IP students managing accelerated school curricula and deeper conceptual expectations.';
    case 'ib':
      return 'IB students working on the IB Mathematics curriculum, at a pace and depth the programme demands.';
    default:
      return `${levelLabel} students who need clear weekly structure and targeted practice.`;
  }
}

interface LevelContent {
  id: string;
  slug: string;
  seo: {
    title: string;
    description: string;
  };
  hero: {
    eyebrow?: string;
    headline?: string;
    subhead?: string;
  };
  syllabus?: LevelSyllabus;
  challenges?: LevelChallenges;
  howWeHelp?: LevelApproach;
  approach?: LevelApproach;
  streamSelector?: object;
  faqItems?: FaqItem[];
  decisionProofPoints?: DecisionProofPoint[];
}

/**
 * A page that supplies no section content at all still has to render, so the
 * layout gets an empty, correctly-shaped block rather than a bare `{}`.
 */
const EMPTY_SYLLABUS: LevelPageLayoutContent['syllabus'] = {
  title: '',
};

const EMPTY_CHALLENGES: LevelPageLayoutContent['challenges'] = {
  title: '',
  items: [],
};

const EMPTY_APPROACH: LevelPageLayoutContent['approach'] = {
  title: '',
  points: [],
};

function buildProgramFacts({
  levelLabel,
  pageCategory,
}: {
  levelLabel: string;
  pageCategory: PageCategory;
}): LevelPageLayoutContent['programFacts'] {
  const syllabusLabel = inferSyllabusLabel(pageCategory, levelLabel);
  const facts: LevelPageLayoutContent['programFacts']['facts'] = [
    {
      label: 'Lesson format',
      value: 'Weekly 1.5-hour live online class',
      detail:
        'Targeted online practice with instant marking supports work between lessons.',
    },
    CLASS_SIZE_FACT,
    {
      label: 'Syllabus focus',
      value: syllabusLabel,
      detail:
        'Full curriculum and chapter list shown in the syllabus section above.',
    },
    {
      label: 'Pricing',
      value: '$50 per live class',
      detail: 'Same fee across levels and streams.',
    },
    {
      label: 'Trial',
      value: 'Free trial class available',
      detail:
        'Parents can see the teaching pace, structure, and student experience before committing.',
    },
  ];

  // Secondary hub pages have enough "compare routes" messaging elsewhere, so
  // we omit the "Best fit" meta to keep the facts section neutral.
  if (pageCategory !== 'secondaryHub') {
    facts.push({
      label: 'Best fit',
      value: inferBestFit(pageCategory, levelLabel),
      detail: undefined,
    });
  }

  return {
    eyebrow: 'Program facts',
    title: `What families should know about ${levelLabel} support`,
    body: 'The details parents usually want before deciding whether to book a trial.',
    facts,
  };
}

function buildDecisionSupport(
  levelContent: LevelContent,
  { levelLabel }: { levelLabel: string; pageCategory: PageCategory }
): LevelPageLayoutContent['decisionSupport'] {
  // Prefer the page-specific proof points the content module attached; fall
  // back to the first three challenge items for pages that don't provide any.
  const proofPoints =
    levelContent.decisionProofPoints ??
    (levelContent.challenges?.items || []).slice(0, 3).map(item => ({
      title: item.title,
      description: item.description,
    }));

  return {
    eyebrow: 'Decision support',
    title: `When ${levelLabel} support is the right fit`,
    body: 'These are the situations where extra support tends to make the biggest difference.',
    proofPoints,
    closingLine:
      'If any of these patterns sound familiar, this is likely the right level of support for your child — a trial class is a good next step.',
  };
}

/**
 * Appends the class-size answer to a page's FAQ set unless the page already
 * answers it. Class size is the question parents ask first, so it belongs on
 * every level page — including the pages that supply their own FAQ set — and
 * it feeds each page's FAQPage JSON-LD.
 */
function withClassSizeFaq(items: FaqItem[]): FaqItem[] {
  const alreadyAnswered = items.some(
    item => item.question === CLASS_SIZE_FAQ.question
  );
  return alreadyAnswered ? items : [...items, CLASS_SIZE_FAQ];
}

function buildFaqItems(
  levelContent: LevelContent,
  {
    levelLabel,
    pageCategory,
  }: { levelLabel: string; pageCategory: PageCategory }
): LevelPageLayoutContent['faqItems'] {
  // Prefer the page-specific FAQ set the content module attached (secondary
  // stream, PSLE, IP, and per-level primary pages all provide their own). Pages
  // that don't attach one fall through to the generic templated set below.
  if (levelContent.faqItems) {
    return withClassSizeFaq(levelContent.faqItems);
  }

  const items = [
    {
      question: `Who is ${levelLabel} Math tuition for?`,
      answer: inferBestFit(pageCategory, levelLabel),
    },
    {
      question: `What does weekly ${levelLabel} support include?`,
      answer:
        'Students attend a 1.5-hour live online class each week and then work through targeted online practice with instant marking and solutions between lessons.',
    },
    {
      question: `Which syllabus or exam does this ${levelLabel} page cover?`,
      answer:
        levelContent.syllabus?.body || levelContent.seo?.description || '',
    },
    {
      question: 'Can families start with a trial class first?',
      answer:
        'Yes. Families can start with a trial class to see the teaching pace, structure, and level fit before committing.',
    },
    {
      question: 'How do parents know what to work on next?',
      answer:
        'We keep parents informed on what was covered and where their child should focus. If we spot persistent gaps, we flag them early — not just before exams.',
    },
  ];

  // Secondary hub pages keep the FAQ focused on what changes week-to-week;
  // route-comparison messaging lives in the facts/links areas.
  return withClassSizeFaq(
    pageCategory === 'secondaryHub' ? items.slice(1) : items
  );
}

/**
 * Converts one level content entry into the `LevelPageLayout` prop format.
 */
export function toLevelPageLayoutContent(
  levelContent: LevelContent,
  { levelLabel }: { levelLabel?: string } = {}
): LevelPageLayoutContent {
  if (!levelContent) {
    throw new Error(
      'Level content is required to build LevelPageLayout content.'
    );
  }

  const resolvedLevelLabel = levelLabel ?? levelContent.id;
  const normalizedSlug = levelContent.slug.replace(/^\/+/u, '');
  const pageCategory = inferPageCategory(levelContent.slug);
  const searchIntentHeading = `${stripBrandSuffix(levelContent.seo.title)} in Singapore`;
  const intro = levelContent.seo.description;

  return {
    id: levelContent.id,
    slug: normalizedSlug,
    level: resolvedLevelLabel,
    title: searchIntentHeading,
    metaTitle: levelContent.seo.title,
    metaDescription: levelContent.seo.description,
    searchIntentHeading,
    intro,
    hero: {
      eyebrow: levelContent.hero.eyebrow,
      headline: levelContent.hero.headline ?? searchIntentHeading,
      subheadline: levelContent.hero.subhead ?? levelContent.seo.description,
    },
    ...(levelContent.streamSelector && {
      streamSelector:
        levelContent.streamSelector as LevelPageLayoutContent['streamSelector'],
    }),
    syllabus: levelContent.syllabus ?? EMPTY_SYLLABUS,
    challenges: levelContent.challenges ?? EMPTY_CHALLENGES,
    approach: levelContent.howWeHelp ?? levelContent.approach ?? EMPTY_APPROACH,
    programFacts: buildProgramFacts({
      levelLabel: resolvedLevelLabel,
      pageCategory,
    }),
    decisionSupport: buildDecisionSupport(levelContent, {
      levelLabel: resolvedLevelLabel,
      pageCategory,
    }),
    faqItems: buildFaqItems(levelContent, {
      levelLabel: resolvedLevelLabel,
      pageCategory,
    }),
  };
}
